<script setup>
/* global axios */
import { computed, ref, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'dashboard/composables/store.js';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const store = useStore();

const inboxName = ref('');
const phoneNumber = ref('');
const manageGroups = ref(false);
const qrCode = ref('');
const connectionStatus = ref('');
const isCreating = ref(false);
const isConnecting = ref(false);
const inboxId = ref(null);
const pollInterval = ref(null);
const errorMessage = ref('');

const accountId = route.params.accountId;
const baseUrl = `/api/v1/accounts/${accountId}`;
const normalizeQrCode = value => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value !== 'object') return String(value);

  return value.base64 || value.code || value.qrcode?.base64 || '';
};
const qrCodeSource = computed(() => {
  if (!qrCode.value) return '';

  return qrCode.value.startsWith('data:')
    ? qrCode.value
    : `data:image/png;base64,${qrCode.value}`;
});

const stopPolling = () => {
  if (pollInterval.value) {
    clearInterval(pollInterval.value);
    pollInterval.value = null;
  }
};

const fetchQrCode = async () => {
  if (!inboxId.value) return;

  try {
    const { data } = await axios.get(
      `${baseUrl}/inboxes/${inboxId.value}/evolution/qrcode`
    );
    if (data.qrcode) qrCode.value = normalizeQrCode(data.qrcode);
    if (data.status) connectionStatus.value = data.status;
  } catch {
    // Silently retry on next poll
  }
};

const checkStatus = async () => {
  if (!inboxId.value) return;

  try {
    const { data } = await axios.get(
      `${baseUrl}/inboxes/${inboxId.value}/evolution/status`
    );
    connectionStatus.value = data.status;

    if (data.status === 'open') {
      stopPolling();
      useAlert(t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.CONNECTED'));
      router.replace({
        name: 'settings_inboxes_add_agents',
        params: { page: 'new', inbox_id: inboxId.value },
      });
    }
  } catch {
    // Retry on next poll
  }
};

const startPolling = () => {
  pollInterval.value = setInterval(async () => {
    await fetchQrCode();
    await checkStatus();
  }, 5000);
};

const createAndConnect = async () => {
  if (!inboxName.value.trim()) {
    errorMessage.value = t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.ERROR');
    return;
  }

  isCreating.value = true;
  errorMessage.value = '';

  try {
    // 1. Create the inbox with evolution provider
    const response = await store.dispatch('inboxes/createChannel', {
      name: inboxName.value.trim(),
      channel: {
        type: 'whatsapp',
        phone_number: phoneNumber.value || `+evo_${Date.now()}`,
        provider: 'evolution',
        provider_config: {
          manage_groups: manageGroups.value,
        },
      },
    });

    inboxId.value = response.id;

    // 2. Create instance on Evolution API
    isConnecting.value = true;
    const { data: instanceData } = await axios.post(
      `${baseUrl}/inboxes/${response.id}/evolution/create_instance`
    );

    if (instanceData.error) {
      errorMessage.value = instanceData.error;
      isConnecting.value = false;
      isCreating.value = false;
      return;
    }

    qrCode.value = normalizeQrCode(instanceData.qrcode);
    connectionStatus.value = 'connecting';

    // 3. Start polling for QR code and connection status
    startPolling();
  } catch (error) {
    errorMessage.value =
      error.message || t('INBOX_MGMT.ADD.WHATSAPP.API.ERROR_MESSAGE');
  } finally {
    isCreating.value = false;
  }
};

onUnmounted(stopPolling);
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- Step 1: Form -->
    <form
      v-if="!isConnecting"
      class="flex flex-col gap-4"
      @submit.prevent="createAndConnect"
    >
      <label>
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.LABEL') }}
        <input
          v-model="inboxName"
          type="text"
          :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.PLACEHOLDER')"
        />
      </label>

      <label>
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.PHONE_LABEL') }}
        <input
          v-model="phoneNumber"
          type="text"
          :placeholder="
            $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.PHONE_PLACEHOLDER')
          "
        />
        <span class="text-xs text-n-slate-11">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.PHONE_HINT') }}
        </span>
      </label>

      <label
        class="flex gap-3 rounded-lg border border-n-weak bg-n-solid-2 p-3"
      >
        <input
          v-model="manageGroups"
          type="checkbox"
          class="mt-1 size-4 flex-shrink-0"
        />
        <span class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.GROUPS_LABEL') }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.GROUPS_HINT') }}
          </span>
        </span>
      </label>

      <p v-if="errorMessage" class="text-r-500 text-sm">
        {{ errorMessage }}
      </p>

      <NextButton
        :is-loading="isCreating"
        type="submit"
        solid
        blue
        :label="$t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.CREATE_BUTTON')"
      />
    </form>

    <!-- Step 2: QR Code -->
    <div v-else class="flex flex-col items-center gap-6">
      <div class="text-center">
        <h2 class="text-lg font-medium text-n-slate-12 mb-2">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.SCAN_TITLE') }}
        </h2>
        <p class="text-sm text-n-slate-11">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.SCAN_DESCRIPTION') }}
        </p>
      </div>

      <div
        v-if="qrCodeSource"
        class="p-4 bg-white rounded-2xl border border-n-weak shadow-sm"
      >
        <img :src="qrCodeSource" alt="WhatsApp QR Code" class="w-64 h-64" />
      </div>
      <div
        v-else
        class="w-64 h-64 rounded-2xl bg-n-solid-3 flex items-center justify-center"
      >
        <span class="text-n-slate-11 text-sm">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.LOADING_QR') }}
        </span>
      </div>

      <div class="flex items-center gap-2">
        <span
          class="size-2 rounded-full"
          :class="{
            'bg-y-500': connectionStatus === 'connecting',
            'bg-g-500': connectionStatus === 'open',
            'bg-r-500': connectionStatus === 'close',
          }"
        />
        <span class="text-sm text-n-slate-11">
          {{
            connectionStatus === 'open'
              ? $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.STATUS_CONNECTED')
              : $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.STATUS_WAITING')
          }}
        </span>
      </div>

      <p v-if="errorMessage" class="text-r-500 text-sm">
        {{ errorMessage }}
      </p>
    </div>
  </div>
</template>
